import React, { useState, useEffect } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import { useNavigate } from 'react-router-dom';
import ciMetricsService, { AggregateMetrics, RepoPassRate, SustainedFailure } from '../services/ciMetricsService';

import './CIPassRateReportsPage.css';
import { isUserAdmin } from '../helpers/adminAuth';

export const CIPassRateReportsPage: React.FC = () => {
  const { authState } = useOktaAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  // Data states
  const [aggregateData, setAggregateData] = useState<AggregateMetrics | null>(null);
  const [belowThresholdData, setBelowThresholdData] = useState<RepoPassRate[]>([]);
  const [sustainedFailures, setSustainedFailures] = useState<SustainedFailure[]>([]);

  // Filter states
  const [days, setDays] = useState<number>(14);
  const [threshold, setThreshold] = useState<number>(80);
  const [sustainedThreshold, setSustainedThreshold] = useState<number>(3);

  useEffect(() => {
    const fetchMetrics = async () => {
      if (authState?.isPending) return;
      
      setLoading(true);
      setError(null);

      try {
        const [aggRes, belowRes, sustainedRes] = await Promise.all([
          ciMetricsService.getAggregate(authState, days),
          ciMetricsService.getBelowThreshold(authState, threshold, days),
          ciMetricsService.getSustainedFailures(authState, sustainedThreshold)
        ]);

        setAggregateData(aggRes);
        setBelowThresholdData(belowRes.repos || []);
        setSustainedFailures(sustainedRes.failures || []);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to load CI metrics';
        setError(message);
        console.error('Error fetching CI metrics:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchMetrics();
  }, [days, threshold, sustainedThreshold, authState?.isPending, authState]);

  const handleExportCsv = async () => {
    try {
      setExporting(true);
      await ciMetricsService.exportCsv(authState, days);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to export CSV';
      setError(message);
    } finally {
      setExporting(false);
    }
  };

  const formatDate = (dateString?: string): string => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Invalid';
      return date.toLocaleDateString();
    } catch {
      return 'Invalid';
    }
  };

  const getScoreBadge = (score: number) => {
    if (score >= 90) return 'ci-badge-compliant';
    if (score >= 70) return 'ci-badge-partially-compliant';
    return 'ci-badge-non-compliant';
  };

  if (authState?.isPending) {
    return <div className="container mt-4"><div className="loading">Verifying permissions...</div></div>;
  }

  if (!isUserAdmin(authState)) {
      return (
        <div className="container mt-4">
          <div className="alert alert-error">
            <h4>Access Denied</h4>
            <p>You do not have permission to access the Reports page. Only admins can view this section.</p>
          </div>
        </div>
      );
  }
  
  return (
    <div className="ci-reports-page">
      {error && (
        <div className="ci-alert ci-alert-error">
          <span>{error}</span>
          <button type="button" className="ci-alert-close" aria-label="Dismiss error" onClick={() => setError(null)}>✕</button>
        </div>
      )}

      <div className="ci-reports-container">
        {/* Header */}
        <div className="ci-reports-header">
          <h1>CI Pass Rate Reports</h1>
          <p>Analyze API Governance pipeline validation metrics across product repositories.</p>
        </div>

        {/* Filter Bar */}
        <div className="ci-filter-bar">
          <div className="ci-filter-group">
            <label>Time Window:</label>
            <select value={days} onChange={(e) => setDays(Number(e.target.value))}>
              <option value={7}>Last 7 Days</option>
              <option value={14}>Last 14 Days</option>
              <option value={30}>Last 30 Days</option>
              <option value={90}>Last 90 Days</option>
            </select>
          </div>
          
          <div className="ci-filter-group">
            <label>Pass Rate Threshold:</label>
            <select value={threshold} onChange={(e) => setThreshold(Number(e.target.value))}>
              <option value={100}>100% (Any Failures)</option>
              <option value={90}>Under 90%</option>
              <option value={80}>Under 80%</option>
              <option value={70}>Under 70%</option>
            </select>
          </div>

          <div className="ci-filter-group">
            <label>Sustained Failures:</label>
            <select value={sustainedThreshold} onChange={(e) => setSustainedThreshold(Number(e.target.value))}>
              <option value={2}>2+ Consecutive</option>
              <option value={3}>3+ Consecutive</option>
              <option value={5}>5+ Consecutive</option>
            </select>
          </div>
        </div>

        {loading && !aggregateData ? (
          <div className="ci-no-data">Loading CI Metrics Data...</div>
        ) : (
          <>
            {/* Top Aggregate Stats */}
            {aggregateData && (
              <div className="ci-stats-row">
                <div className="ci-stat-card">
                  <h3>Overall Pass Rate</h3>
                  <div>
                    <span className={`ci-badge ci-badge-large ${getScoreBadge(aggregateData.overall.passRate)}`}>
                      {aggregateData.overall.passRate}%
                    </span>
                  </div>
                  <p>{aggregateData.overall.passed} Passed / {aggregateData.overall.total} Total Runs</p>
                </div>
                
                <div className="ci-stat-card">
                  <h3>GitHub Pass Rate</h3>
                  <div>
                    <span className={`ci-badge ci-badge-large ${getScoreBadge(aggregateData.byPlatform.github.passRate)}`}>
                      {aggregateData.byPlatform.github.passRate}%
                    </span>
                  </div>
                  <p>{aggregateData.byPlatform.github.passed} / {aggregateData.byPlatform.github.total} Runs</p>
                </div>

                <div className="ci-stat-card">
                  <h3>Enhanced Mode Pass Rate</h3>
                  <div>
                    <span className={`ci-badge ci-badge-large ${getScoreBadge(aggregateData.byMode.enhanced.passRate)}`}>
                      {aggregateData.byMode.enhanced.passRate}%
                    </span>
                  </div>
                  <p>{aggregateData.byMode.enhanced.passed} / {aggregateData.byMode.enhanced.total} Runs</p>
                </div>
              </div>
            )}

            {/* Repositories Below Threshold Table */}
            <div className="ci-table-section">
              <h3>Repositories Below {threshold}% Pass Rate</h3>
              <div className="ci-table-wrapper" style={{ maxHeight: '350px' }}>
                {belowThresholdData.length === 0 ? (
                  <div className="ci-no-data">No repositories are below the threshold in this window.</div>
                ) : (
                  <table className="ci-table">
                    <thead>
                      <tr>
                        <th>Repository</th>
                        <th>Pass Rate</th>
                        <th>Passed / Total</th>
                        <th>Blocked Runs</th>
                        <th>Runtime Errors</th>
                        <th>Last Run</th>
                      </tr>
                    </thead>
                    <tbody>
                      {belowThresholdData.map((repo, idx) => (
                        <tr key={idx}>
                          <td><strong>{repo.repo}</strong></td>
                          <td>
                            <span className={`ci-badge ${getScoreBadge(repo.passRate)}`}>
                              {repo.passRate}%
                            </span>
                          </td>
                          <td>{repo.passed} / {repo.total}</td>
                          <td style={{ color: repo.blocked > 0 ? '#cc0000' : 'inherit', fontWeight: repo.blocked > 0 ? 'bold' : 'normal' }}>
                            {repo.blocked}
                          </td>
                          <td style={{ color: repo.runtimeErrors > 0 ? '#cc0000' : 'inherit', fontWeight: repo.runtimeErrors > 0 ? 'bold' : 'normal' }}>
                            {repo.runtimeErrors}
                          </td>
                          <td>{formatDate(repo.lastRun)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            {/* Sustained Failures Table */}
            <div className="ci-table-section">
              <h3>Sustained Failures ({sustainedThreshold}+ Consecutive)</h3>
              <div className="ci-table-wrapper">
                {sustainedFailures.length === 0 ? (
                  <div className="ci-no-data">No active sustained failures detected.</div>
                ) : (
                  <table className="ci-table">
                    <thead>
                      <tr>
                        <th>Repository</th>
                        <th>Consecutive Failures</th>
                        <th>First Failure</th>
                        <th>Last Failure</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sustainedFailures.map((failure, idx) => (
                        <tr key={idx}>
                          <td><strong>{failure.repo}</strong></td>
                          <td>
                            <span className="ci-badge ci-badge-non-compliant">
                              {failure.consecutiveFailures} Runs
                            </span>
                          </td>
                          <td>{formatDate(failure.firstFailureDate)}</td>
                          <td>{formatDate(failure.lastFailureDate)}</td>
                          <td>
                            <a 
                              href={failure.lastRunUrl} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="ci-btn ci-btn-link"
                            >
                              View CI Logs ↗
                            </a>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </>
        )}

        {/* Action Buttons Footer */}
        <div className="ci-action-buttons">
          <button className="ci-btn ci-btn-secondary" onClick={() => navigate('/admin/reports')}>
            ← Back to Governance Reports
          </button>
          <button 
            className="ci-btn ci-btn-primary" 
            onClick={handleExportCsv}
            disabled={exporting || loading}
          >
            {exporting ? 'Exporting...' : 'Export to CSV'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CIPassRateReportsPage;